<template>
  <div class="key-action-container">
    <button
      type="button"
      class="btn btn-primary full-width"
      aria-describedby="generate-key-warning"
      :disabled="isLoading || disabled"
      :aria-disabled="isLoading || disabled ? 'true' : 'false'"
      @click="createApiKey"
    >
      <i class="fas fa-plus" aria-hidden="true"></i>
      {{ isLoading ? 'Generating…' : 'Generate new API Key' }}
    </button>
    <p id="generate-key-warning" class="generate-key-warning" role="note">
      <i class="fas fa-exclamation-triangle amber" aria-hidden="true"></i>
      The key is displayed <b class="amber">only once</b>. Please make sure to <b>copy</b> it before closing the page.
    </p>

    <k-modal :show="show" @close="close">
      <template #header>
        <h3 id="generate-key-modal-title">Generated API Key</h3>
      </template>
      <template #body>
        <p id="generate-key-modal-description">
          This is the only time this key will be shown. Save it now — never share it or commit it to a repository.
        </p>
        <label for="api-key-text">API Key:</label>
        <div class="api-key-field">
          <input
            id="api-key-text"
            v-model="newApiKey"
            type="text"
            readonly
            aria-describedby="generate-key-modal-description"
            @focus="$event.target.select()"
          >
          <button
            type="button"
            class="btn btn-outlined copy-to-clipboard"
            :disabled="!newApiKey"
            aria-describedby="copy-api-key-status"
            @click="copyApiKey"
          >
            <i class="fa-regular fa-copy" aria-hidden="true"></i> Copy
          </button>
        </div>
        <span id="copy-api-key-status" class="sr-only" role="status" aria-live="polite">{{ copyStatusMessage }}</span>
        <div class="disclaimer">
          <input
            id="disclaimer-checkbox"
            v-model="disclaimerChecked"
            type="checkbox"
            required
            aria-required="true"
            aria-describedby="create-api-key-hint"
          >
          <label for="disclaimer-checkbox">
            I have stored this key somewhere safe and understand it cannot be recovered.
          </label>
        </div>
        <span id="create-api-key-hint" class="sr-only">
          {{ disclaimerChecked ? 'Disclaimer accepted. You can close this dialog.' : 'Confirm you have stored the key before closing.' }}
        </span>
      </template>
      <template #footer>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!disclaimerChecked"
          :aria-disabled="!disclaimerChecked ? 'true' : 'false'"
          aria-describedby="create-api-key-hint"
          @click="close"
        >
          Done
        </button>
      </template>
    </k-modal>
  </div>
</template>

<style scoped>
.generate-key-warning {
  margin-top: 15px;
}

.api-key-field {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.api-key-field input {
  flex: 1 1 12rem;
  min-width: 0;
}

.disclaimer {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  margin-top: 1rem;
}

.disclaimer input {
  margin-top: 0.2rem;
}

.full-width {
  width: 100%;
}
</style>

<script>
import KModal from '@base-components/k-modal.vue';
import { copyToClipboard } from '@utils/copy-to-clipboard';

export default {
  name: 'GenerateKey',
  components: {
    KModal,
  },

  props: {
    programmeId: {
      type: Number,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['key-created'],

  data() {
    return {
      show: false,
      newApiKey: null,
      disclaimerChecked: false,
      copyStatusMessage: '',
      isLoading: false,
    };
  },

  methods: {
    close() {
      this.show = false;
      this.newApiKey = null;
      this.disclaimerChecked = false;
      this.copyStatusMessage = '';
    },

    copyApiKey() {
      if (!this.newApiKey) {
        return;
      }
      copyToClipboard(this.newApiKey);
      this.copyStatusMessage = 'API key copied to clipboard';
      this.$ktoast.success('API key copied to clipboard');
    },

    createApiKey() {
      if (this.isLoading || this.disabled) {
        return;
      }

      this.$logger.info('Creating API Key', { programmeId: this.programmeId });
      this.isLoading = true;
      this.$Loading.minimal();

      this.$http.post(`/api/keymaker/api-keys/programme/${this.programmeId}`)
        .then(result => {
          const keyData = result.data.key_data;
          this.newApiKey = keyData.secret;
          this.copyStatusMessage = 'API key created. Copy it now; it will not be shown again.';
          this.show = true;
          this.$ktoast.success('API Key created successfully');
          this.$emit('key-created', keyData);
          this.$nextTick(() => {
            document.getElementById('api-key-text')?.focus();
          });
        })
        .catch(error => {
          this.$logger.error('Error creating API Key', { programmeId: this.programmeId }, error);
          this.$ktoast.showError(error);
        })
        .then(() => {
          this.isLoading = false;
          this.$Loading.finish();
        });
    },
  },
};
</script>
